import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { styled, keyframes, alpha } from '@mui/material/styles';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  IconButton,
  Stack
} from '@mui/material';
import { FaGoogle, FaFacebook, FaApple } from 'react-icons/fa';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../../assets/Logo.png';

const gradientAnimation = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const floatingBubbles = keyframes`
  0% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-20px) scale(1.1); }
  100% { transform: translateY(0) scale(1); }
`;

const LoginContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  minWidth: '100vw',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  background: 'linear-gradient(-45deg, #60a5fa, #34d399, #3b82f6, #f472b6)',
  backgroundSize: '400% 400%',
  animation: `${gradientAnimation} 15s ease infinite`,
  position: 'relative',
  overflow: 'hidden',

  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    width: '500px', // Increased size
    height: '500px', // Increased size
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    animation: `${floatingBubbles} 6s infinite ease-in-out`,
    [theme.breakpoints.down('sm')]: {
      width: '300px',
      height: '300px',
    }
  },

  '&::before': {
    top: '-100px',
    left: '-100px',
    animationDelay: '0s',
  },

  '&::after': {
    bottom: '-100px',
    right: '-100px',
    animationDelay: '-3s',
  },
}));

const LoginCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 450,
  minWidth: 320,
  borderRadius: theme.spacing(2),
  backdropFilter: 'blur(10px)',
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha(theme.palette.background.paper, 0.8)
    : 'rgba(255, 255, 255, 0.9)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  position: 'relative',
  zIndex: 1,
  transition: 'transform 0.3s ease-in-out',
  margin: 'auto',
  
  '& .MuiTypography-root': {
    color: theme.palette.text.primary,
  },
  
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
  },
  
  '& .MuiInputBase-input': {
    color: theme.palette.text.primary,
  },

  '&:hover': {
    transform: 'translateY(-5px)',
  },

  [theme.breakpoints.up('lg')]: {
    maxWidth: 500,
  },

  [theme.breakpoints.down('sm')]: {
    maxWidth: '95%',
    margin: theme.spacing(2),
  }
}));

const LogoContainer = styled(Box)({
  textAlign: 'center',
  marginBottom: 24
});

const Logo = styled('img')(({ theme }) => ({
  width: 80,
  height: 80,
  marginBottom: theme.spacing(2),
  transition: 'transform 0.3s ease-in-out',
  
  '&:hover': {
    transform: 'scale(1.1)',
  },

  [theme.breakpoints.down('sm')]: {
    width: 60,
    height: 60,
  }
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  border: '1px solid',
  borderColor: theme.palette.divider,
  borderRadius: '50%',
  padding: 12,
  transition: 'all 0.3s ease-in-out',
  
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    transform: 'translateY(-3px)',
  },

  [theme.breakpoints.down('sm')]: {
    padding: 8,
  }
}));

const ContentWrapper = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(4),
  
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

const CenterWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  minHeight: '100vh',
  padding: theme.spacing(3),
  position: 'relative',
  zIndex: 2,

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  }
}));

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Check token on component load
  useEffect(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (token) {
      navigate('/HomePage');
    }
  }, [navigate]);

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsLoading(true);

  try {
    const apiUrl = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
    const res = await axios.post(`${apiUrl}/auth/login`, { email, password });

    // Support both shapes: {success, data:{token,user}} and {status, token, user}
    const ok = res.data?.success === true || res.data?.status === 'success';
    const payload = res.data?.data ?? res.data ?? {};
    const token = payload.token;
    const user  = payload.user;
    const tokenType = (payload.token_type || 'Bearer').toLowerCase();

    if (!ok || !token || !user) {
      throw new Error(res.data?.message || 'Invalid login response');
    }

    // Persist auth (no brittle JWT dot check)
    const store = rememberMe ? localStorage : sessionStorage;
    store.setItem('token', token);
    store.setItem('token_type', tokenType);     // optional, useful later
    store.setItem('user', JSON.stringify(user));
    if (payload.expires_in) {
      store.setItem('token_expires_at', String(Date.now() + payload.expires_in * 1000));
    }

    // Set axios default Authorization for the session
    axios.defaults.headers.common.Authorization = `${tokenType} ${token}`;

    // Go to Home
    navigate('/HomePage');
  } catch (err) {
    console.error(err);
    if (err.response) {
      toast.error(err.response.data?.message || `Login failed (${err.response.status})`);
    } else if (err.request) {
      toast.error('Network error. Please check your connection.');
    } else {
      toast.error(err.message || 'Login failed');
    }
  } finally {
    setIsLoading(false);
  }
};


  return (
    <LoginContainer>
      <ToastContainer />
      <CenterWrapper>
        <LoginCard>
          <ContentWrapper>
            <LogoContainer>
              <Logo src={logo} alt="Investment Logo" />
              <Typography variant="h4" gutterBottom>
                Welcome Back
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Sign in to manage your investments
              </Typography>
            </LogoContainer>

            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="You@example.com"
                  required
                  disabled={isLoading}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: { xs: 45, sm: 56 },
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter Your Password"
                  required
                  disabled={isLoading}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: { xs: 45, sm: 56 },
                    }
                  }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        disabled={isLoading}
                      />
                    }
                    label="Remember me"
                  />
                  <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                    <Typography color="primary">Forgot password?</Typography>
                  </Link>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  type="submit"
                  disabled={isLoading}
                  sx={{
                    height: { xs: 45, sm: 56 },
                    fontSize: { xs: '0.9rem', sm: '1rem' }
                  }}
                >
                  {isLoading ? 'Signing In...' : 'Sign In'}
                </Button>
              </Stack>
            </form>

            <Box sx={{ my: 3 }}>
              <Divider>
                <Typography color="text.secondary" sx={{ px: 2 }}>
                  or continue with
                </Typography>
              </Divider>
            </Box>

            <Stack direction="row" spacing={2} justifyContent="center">
              <SocialButton>
                <FaGoogle />
              </SocialButton>
              <SocialButton>
                <FaFacebook />
              </SocialButton>
              <SocialButton>
                <FaApple />
              </SocialButton>
            </Stack>

            <Typography align="center" sx={{ mt: 3 }}>
              Don't have an account?{' '}
              <Link to="/registered" style={{ textDecoration: 'none' }}>
                <Typography component="span" color="primary">
                  Create One
                </Typography>
              </Link>
            </Typography>
          </ContentWrapper>
        </LoginCard>
      </CenterWrapper>
    </LoginContainer>
  );
};

export default Login;
